import React from 'react';
import { Cart, ShippingMethod } from '../types';
import ErrorBoundary from './ErrorBoundary';
import { Icon } from './checkout/icons';

interface OrderSummaryProps {
  cart: Cart;
  orderSummary: {
    subtotal: number;
    shipping: number;
    tax: number;
    total: number;
    currency: string;
  } | null;
  shippingMethod?: ShippingMethod;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  cart,
  orderSummary,
  shippingMethod,
}) => {
  const formatPrice = (amount: number, currency: string) => {
    try {
      const locale = currency.toLowerCase() === 'sek' ? 'sv-SE' : 'en-US';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency.toUpperCase(),
      }).format(amount / 100);
    } catch (error) {
      console.warn('Error formatting price:', error);
      // Fallback without hardcoded currency symbol
      return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
    }
  };

  // Validate cart data
  if (!cart) {
    return (
      <div className="order-summary">
        <h3>Order Summary</h3>
        <div className="error-state" style={{
          padding: '20px',
          textAlign: 'center',
          color: 'var(--color-text-secondary)',
          backgroundColor: 'var(--color-bg-secondary)',
          borderRadius: 'var(--radius-md, 8px)',
        }}>
          <div aria-hidden="true" style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px', color: 'var(--color-error)' }}>
            <Icon name="alert-circle" size={24} />
          </div>
          <p style={{ margin: 0 }}>Cart data is not available</p>
          <p style={{ fontSize: '14px' }}>Please try refreshing the page or adding items to your cart.</p>
        </div>
      </div>
    );
  }

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="order-summary">
        <h3>Order Summary</h3>
        <div className="empty-state" style={{
          padding: '20px',
          textAlign: 'center',
          color: '#666',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
        }}>
          <p>🛒 Your cart is empty</p>
          <p style={{ fontSize: '14px' }}>Add some products to continue with checkout.</p>
        </div>
      </div>
    );
  }

  if (!orderSummary) {
    return (
      <div className="order-summary">
        <h3>Order Summary</h3>
        <div className="loading">Calculating totals...</div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="order-summary">
        <h3>Order Summary</h3>

        {/* Cart Items */}
        <div className="cart-items">
          {cart.items.map((item) => {
            // Robust error handling for each cart item
            try {
              const productTitle = item.product?.title || 'Unknown Product';
              const variantTitle = item.variant?.title || null;
              const itemTotal = item.unit_price * item.quantity;
              
              return (
                <div key={item.id} className="cart-item">
                  <div className="item-info">
                    <h4>{productTitle}</h4>
                    {variantTitle && (
                      <p className="variant">{variantTitle}</p>
                    )}
                    {!item.variant && (
                      <p className="variant" style={{ color: '#999', fontSize: '12px' }}>
                        Default variant
                      </p>
                    )}
                    <p className="quantity">Qty: {item.quantity}</p>
                  </div>
                  <div className="item-price">
                    {formatPrice(itemTotal, cart.currency_code)}
                  </div>
                </div>
              );
            } catch (error) {
              console.error('Error rendering cart item:', error, item);
              return (
                <div key={item.id || Math.random()} className="cart-item">
                  <div className="item-info">
                    <h4 style={{ color: '#ff6b6b' }}>⚠️ Error loading item</h4>
                    <p style={{ fontSize: '12px', color: '#666' }}>
                      Item ID: {item.id || 'unknown'}
                    </p>
                  </div>
                  <div className="item-price">
                    <span style={{ color: '#ff6b6b' }}>Error</span>
                  </div>
                </div>
              );
            }
          })}
        </div>

      {/* Totals */}
      <div className="order-totals">
        <div className="total-line">
          <span>Subtotal ({cart.items.length} items)</span>
          <span>{formatPrice(orderSummary.subtotal, orderSummary.currency)}</span>
        </div>

        {shippingMethod && (
          <div className="total-line">
            <span>Shipping ({shippingMethod.name})</span>
            <span>
              {shippingMethod.price === 0 
                ? 'FREE' 
                : formatPrice(orderSummary.shipping, orderSummary.currency)
              }
            </span>
          </div>
        )}

        {!shippingMethod && (
          <div className="total-line">
            <span>Shipping</span>
            <span>Calculated at next step</span>
          </div>
        )}

        <div className="total-line">
          <span>Tax</span>
          <span>{formatPrice(orderSummary.tax, orderSummary.currency)}</span>
        </div>

        <div className="total-line total-final">
          <span><strong>Total</strong></span>
          <span><strong>{formatPrice(orderSummary.total, orderSummary.currency)}</strong></span>
        </div>
      </div>

      {/* Security Badge */}
      <div className="security-info">
        <div className="security-badge">
          🔒 Secure checkout powered by Stripe
        </div>
        <p className="security-text">
          Your payment information is encrypted and secure.
        </p>
      </div>
    </div>
    </ErrorBoundary>
  );
};

export default OrderSummary;